import React from 'react';
import ReportsHeader from '../../Common/ReportsHeader/ReportsHeader';
import Loader from '../../Common/Loader/Loader';
import { firebaseApp } from '../../../js/firebase';
import { getFirestore, collection, getDocs, query, orderBy } from 'firebase/firestore';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import './WastageTracking.css';

const db = getFirestore(firebaseApp);

class WastageTracking extends React.Component {
    
    constructor(props){
        super(props);
        this.state = {
            recipes: [],
            loadingRecipes: false,
            wastedRecipes: [],
            selectedRecipe: null,
            wastedAmount: '',
            ingredientSummary: {}
        }
    }

    componentDidMount(){
        this.loadRecipes();
    }

    loadRecipes = async () => {
        this.setState({ loadingRecipes: true });
        try {
            const recipesRef = collection(db, 'recipes');
            const q = query(recipesRef, orderBy('name'));
            const querySnapshot = await getDocs(q);
            const recipes = [];
            querySnapshot.forEach((doc) => {
                recipes.push({ id: doc.id, ...doc.data() });
            });
            this.setState({ recipes, loadingRecipes: false });
        } catch (error) {
            console.error('Error loading recipes:', error);
            this.setState({ loadingRecipes: false });
        }
    }

    handleAddWastage = () => {
        const { selectedRecipe, wastedAmount, wastedRecipes } = this.state;

        if (!selectedRecipe) {
            alert('Please select a recipe');
            return;
        }

        if (!wastedAmount || isNaN(wastedAmount) || parseFloat(wastedAmount) <= 0) {
            alert('Please enter a valid wasted amount');
            return;
        }

        const wastedQty = parseFloat(wastedAmount);
        const newWastage = {
            id: Date.now(), // temporary ID
            recipeId: selectedRecipe.id,
            recipeName: selectedRecipe.name,
            wastedAmount: wastedQty,
            yieldUnit: selectedRecipe.yieldUnit || 'g',
            recipeYield: selectedRecipe.yield,
            ingredients: selectedRecipe.ingredients || []
        };

        this.setState({
            wastedRecipes: [...wastedRecipes, newWastage],
            selectedRecipe: null,
            wastedAmount: ''
        }, () => {
            this.calculateIngredientSummary();
        });
    }

    removeWastage = (wastageId) => {
        const { wastedRecipes } = this.state;
        this.setState({
            wastedRecipes: wastedRecipes.filter(w => w.id !== wastageId)
        }, () => {
            this.calculateIngredientSummary();
        });
    }

    calculateIngredientSummary = () => {
        const { wastedRecipes } = this.state;
        const ingredientSummary = {};

        wastedRecipes.forEach(wastage => {
            const { wastedAmount, recipeYield, ingredients } = wastage;
            
            // Calculate the ratio of wasted recipe to total recipe yield
            const ratio = wastedAmount / recipeYield;

            // For each ingredient in the recipe, calculate how much was wasted
            ingredients.forEach(ingredient => {
                const ingredientId = ingredient.id;
                const ingredientName = ingredient.name;
                const ingredientSku = ingredient.sku;
                const ingredientUnits = ingredient.units;
                const ingredientQuantity = ingredient.quantity;

                // Calculate wasted amount of this ingredient
                const wastedIngredientAmount = ingredientQuantity * ratio;

                if (!ingredientSummary[ingredientId]) {
                    ingredientSummary[ingredientId] = {
                        id: ingredientId,
                        name: ingredientName,
                        sku: ingredientSku,
                        units: ingredientUnits,
                        totalWasted: 0
                    };
                }

                ingredientSummary[ingredientId].totalWasted += wastedIngredientAmount;
            });
        });

        this.setState({ ingredientSummary });
    }

    renderWastageTable() {
        const { wastedRecipes, loadingRecipes, recipes, selectedRecipe, wastedAmount } = this.state;

        return (
            <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0, bgcolor: '#fafafa', px: 2, py: 1.5, borderRadius: 1 }}>
                    <Typography variant="h5" component="h2">
                        Record Wastage
                    </Typography>
                </Stack>

                <Box sx={{ mt: 2, mb: 3, p: 2, bgcolor: '#fff', borderRadius: 1 }}>
                    <Stack spacing={2}>
                        <Autocomplete
                            options={recipes}
                            getOptionLabel={(option) => `${option.name} (${option.yield} ${option.yieldUnit || 'g'})`}
                            value={selectedRecipe}
                            onChange={(event, newValue) => {
                                this.setState({ selectedRecipe: newValue });
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Recipe"
                                    placeholder="Search recipes..."
                                />
                            )}
                            fullWidth
                        />
                        <Stack direction="row" spacing={2} alignItems="center">
                            <TextField
                                label="Wasted Amount"
                                type="number"
                                value={wastedAmount}
                                onChange={(e) => this.setState({ wastedAmount: e.target.value })}
                                fullWidth
                                helperText={selectedRecipe ? `Enter amount in ${selectedRecipe.yieldUnit || 'g'}` : ''}
                            />
                            <Button 
                                color="success" 
                                variant="contained" 
                                startIcon={<AddIcon />}
                                onClick={this.handleAddWastage}
                                disabled={!selectedRecipe || !wastedAmount}
                                sx={{ minWidth: 120 }}
                            >
                                Add
                            </Button>
                        </Stack>
                    </Stack>
                </Box>

                {wastedRecipes.length > 0 && (
                    <TableContainer component={Paper} sx={{ bgcolor: '#fff', mb: 3 }}>
                        <Table sx={{ minWidth: 650 }} aria-label="wastage table" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', background: '#47908a', color: '#FFF' }}>Recipe</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', background: '#47908a', color: '#FFF' }}>Wasted Amount</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', background: '#47908a', color: '#FFF' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {wastedRecipes.map((wastage) => (
                                    <TableRow key={wastage.id}>
                                        <TableCell>
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                {wastage.recipeName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {wastage.wastedAmount} {wastage.yieldUnit}
                                        </TableCell>
                                        <TableCell>
                                            <Button 
                                                color="error" 
                                                variant="outlined" 
                                                size="small"
                                                startIcon={<DeleteIcon />}
                                                onClick={() => this.removeWastage(wastage.id)}
                                            >
                                                Remove
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        );
    }

    renderIngredientSummary() {
        const { ingredientSummary } = this.state;
        const summaryArray = Object.values(ingredientSummary);

        if (summaryArray.length === 0) {
            return null;
        }

        return (
            <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0, bgcolor: '#fafafa', px: 2, py: 1.5, borderRadius: 1 }}>
                    <Typography variant="h5" component="h2">
                        Ingredient Wastage Summary
                    </Typography>
                </Stack>

                <TableContainer component={Paper} sx={{ bgcolor: '#fff', mt: 2 }}>
                    <Table sx={{ minWidth: 650 }} aria-label="ingredient summary table" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', background: '#47908a', color: '#FFF' }}>SKU</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', background: '#47908a', color: '#FFF' }}>Ingredient Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', background: '#47908a', color: '#FFF' }}>Total Wasted</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', background: '#47908a', color: '#FFF' }}>Units</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {summaryArray.map((ingredient) => (
                                <TableRow key={ingredient.id}>
                                    <TableCell>{ingredient.sku}</TableCell>
                                    <TableCell>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            {ingredient.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="subtitle1" fontWeight="bold" color="error">
                                            {ingredient.totalWasted.toFixed(2)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{ingredient.units}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    }

    render() {
        const { loadingRecipes } = this.state;

        return (
            <div className='wastageTrackingContainer'>
                <ReportsHeader />
                <div className='wastageTrackingContent'>
                    <Box sx={{ padding: 3 }}>
                        {loadingRecipes ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                                <Loader />
                            </Box>
                        ) : (
                            <>
                                {this.renderWastageTable()}
                                {this.renderIngredientSummary()}
                            </>
                        )}
                    </Box>
                </div>
            </div>
        );
    }
}

export default WastageTracking;

